import { FilterDataType } from '@/enums/FilterDataType.enum';
import { FilterDataTypeButton } from '@/models/FilterDataTypeButton.model';
import { falsy, t } from '@/utils/utils';
import { Filters, FilterVal } from '@/models/Filters.model';
import { AuthorsResData, MapParams } from '@/api/endpoints/map/map.types';
import { DOTS_PER_INCH, INCHES_PER_METRE, NULL } from '@/utils/constants.utils';
import { Fill, Stroke, Style } from 'ol/style';
import { Feature } from 'ol';
import { getVectorContext } from 'ol/render';
import VectorLayer from 'ol/layer/Vector';
import Map from 'ol/Map';
import { MapFiltersState } from '@/models/MapFiltersState.model';
import { i18n } from '@/localization/localization';
import { Units, METERS_PER_UNIT } from 'ol/proj/Units';
import VectorSource from 'ol/source/Vector';
import { ColorRange } from '@/models/ColorRange.model';
import { IS_ENV_WEBCOMPONENT } from './constants.utils';

export function getFilterDataTypeButtons(): Array<FilterDataTypeButton> {
	return [
		new FilterDataTypeButton({
			selected: FilterDataType.OBS,
			dataType: FilterDataType.OBS,
			label: t(`filters.dataVisualisation.observation.title`),
		}),
		new FilterDataTypeButton({
			selected: FilterDataType.SCP,
			dataType: FilterDataType.SCP,
			label: t(`filters.dataVisualisation.scenario.title`),
		}),
	];
}


export function getDataTypeLabel(dataType: FilterDataType): string {
    if (!dataType) {
      return '';
    }

    const keyMap = {
      [FilterDataType.OBS]: 'filters.dataType.observation.title',
      [FilterDataType.SCP]: 'filters.dataType.scenario.title',
    };

    return keyMap[dataType] ? t(keyMap[dataType]) : '';
}

export function getSelectedVariableLabel(selected: string): string {
	if (!selected) {
		return '';
	}

	return t(`filters.variable.${selected}Title`);
}

export function getSelectedAuthorLabel(selected: string, authorList: AuthorsResData[]): string {
  if (!selected) {
    return t(`filters.author.defaultLabel`);
  }

	return authorList.find(item => item.slug === selected)?.name || selected;
}

export function getSelectedBasePeriodLabel(selected: string): string {
	if (!selected) {
		return '';
	}
	return t(`filters.period[${JSON.stringify(selected)}]`);
}

export function getSelectedGWLLabel(selected: string): string {
	if (!selected) {
		return '';
	}
	return t(`filters.gwl[${JSON.stringify(selected)}]`);
}

export function getSelectedSeasonLabel(selected: string): string {
	if (!selected) {
		return '';
	}
	return t(`filters.season[${JSON.stringify(selected)}]`);
}

export function getSelectedScenarioLabel(selected: string): string {
	if (!selected) {
		return '';
	}
	return t(`filters.scenario.${selected}`);
}

export function getSelectedGridSpacingLabel(selected: string): string {
	if (!selected) {
		return '';
	}
	return t(`filters.gridSpacing[${JSON.stringify(selected)}]`);
}

export const getFilterVal = (value: FilterVal): unknown => {
	if (!value) {
		return null;
	}
	return typeof value === 'object' ? value.key : value;
};

const FILTER_PARAMS = 'filterParams';

/**
 * Saves filter params in local storage,
 * so the params can be preserved even if a user is changing the routes
 *
 * @param obj
 */
export const saveFilterParams = (obj: MapParams): void => {
	localStorage.setItem(FILTER_PARAMS, JSON.stringify(obj));
};

/**
 * Gets filter params from local storage
 */
export const getFilterParams = (): MapParams => {
	const filterParamsStr = localStorage.getItem(FILTER_PARAMS);
	if (!filterParamsStr || filterParamsStr === NULL) {
		return null;
	}
	const filterParams = JSON.parse(filterParamsStr) as MapParams;
	return falsy(filterParams) ? null : filterParams;
};

/**
 *
 * Set style with color from colorArray based on index from feature prop.
 *
 * @param {Array} colorArray - array of rgb color vals, (eg.) [ "120,10,10", "0, 255, 0",...]
 * @param {Feature} feature - that is getting style
 * @param {string} propIndexName - default: 'index' - name of prop that represents index of feature
 * @param {Stroke} stroke - default: color of fill
 * @param {number} strokeWidth - default: 1
 *
 * @return {Style} - Style
 */
export function getStyleForFeature({
	colorArray,
	feature,
	propIndexName = 'index',
	stroke,
	strokeWidth = 2,
	defaultColor,
}: {
	colorArray: string[];
	feature: Feature;
	propIndexName?: string;
	stroke?: Stroke;
	strokeWidth?: number;
  defaultColor: string;
}): Style {
	const i = feature.get(propIndexName);

	if (!colorArray[i]) {
		return new Style({
			fill: new Fill({
				color: defaultColor,
			}),
      stroke: new Stroke({
        color: defaultColor,
        width: 1,
      }),
		});
	}

	const color = colorArray[i] ? `rgba(${colorArray[i]})` : '#fff';
	const fill = new Fill({
		color,
	});
	// eslint-disable-next-line no-param-reassign
	stroke =
		stroke ||
		new Stroke({
			color,
			width: strokeWidth,
		});

	feature.setProperties({ rgbColor: colorArray[i] });
	feature.setProperties({ color });

	return new Style({
		fill,
		stroke,
	});
}

/**
 * Clips everything out of the borders of maskLayer & shows clippedLayer inside.
 *
 * @param clippedLayer
 * @param maskLayer
 */
export function setClippingLayer({
	clippedLayer,
	maskLayer,
}: {
	clippedLayer: VectorLayer<VectorSource>;
	maskLayer: VectorLayer<VectorSource>;
}): void {
	// Doesn't affects any style, but needs to exist
	const style = new Style({
		fill: new Fill({
			color: '#000',
		}),
	});

	maskLayer.getSource().on('addfeature', () => {
		clippedLayer.setExtent(maskLayer.getSource().getExtent());
	});

	clippedLayer.on('postrender', (e) => {
		const vectorContext = getVectorContext(e);
		if ('globalCompositeOperation' in e.context) {
			e.context.globalCompositeOperation = 'destination-in';
			maskLayer.getSource().forEachFeature((feature) => {
				vectorContext.drawFeature(feature, style);
			});
			e.context.globalCompositeOperation = 'source-over';
		}
	});
}

/**
 * Converts map to canvas so it can be used for download of image or pdf.
 *
 * @param map
 */
export function convertMapToCanvas(map: Map, shadowRoot?: ShadowRoot): HTMLCanvasElement {
	const mapCanvas = document.createElement('canvas');
	const size = map.getSize();
	[mapCanvas.width, mapCanvas.height] = size;
	const mapContext = mapCanvas.getContext('2d');
  const root = IS_ENV_WEBCOMPONENT && shadowRoot ? shadowRoot : document;

	Array.prototype.forEach.call(root.querySelectorAll('.ol-layer canvas'), (canvas) => {
		if (canvas.width > 0) {
			const { opacity } = canvas.parentNode.style;
			mapContext.globalAlpha = opacity === '' ? 1 : Number(opacity);
			const { transform } = canvas.style;
			// Get the transform parameters from the style's transform matrix
			const matrix = transform
				.match(/^matrix\(([^(]*)\)$/)[1]
				.split(',')
				.map(Number);
			// Apply the transform to the export map context
			CanvasRenderingContext2D.prototype.setTransform.apply(mapContext, matrix);
			mapContext.drawImage(canvas, 0, 0);
		}
	});

	return mapCanvas;
}

/**
 * Trims transparent pixels from canvas, so when downloading image, it is smaller.
 *
 * @param canvas
 */
export function trimCanvas(canvas: HTMLCanvasElement) {
	const ctx = canvas.getContext('2d');
	const copy = document.createElement('canvas').getContext('2d');
	const pixels = ctx.getImageData(0, 0, canvas.width, canvas.height);
	const l = pixels.data.length;
	let i;
	const bound: Record<string, number> = {
		top: null,
		left: null,
		right: null,
		bottom: null,
	};
	let x;
	let y;

	// Iterate over every pixel to find the highest
	// and where it ends on every axis ()
	for (i = 0; i < l; i += 4) {
		if (pixels.data[i + 3] !== 0) {
			x = (i / 4) % canvas.width;
			// eslint-disable-next-line no-bitwise
			y = ~~(i / 4 / canvas.width);

			if (bound.top === null) {
				bound.top = y;
			}

			if (bound.left === null) {
				bound.left = x;
			} else if (x < bound.left) {
				bound.left = x;
			}

			if (bound.right === null) {
				bound.right = x;
			} else if (bound.right < x) {
				bound.right = x;
			}

			if (bound.bottom === null) {
				bound.bottom = y;
			} else if (bound.bottom < y) {
				bound.bottom = y;
			}
		}
	}

	// Calculate the height and width of the content
	const trimHeight = bound.bottom - bound.top;
	const trimWidth = bound.right - bound.left;
	const trimmed = ctx.getImageData(bound.left, bound.top, trimWidth, trimHeight);

	copy.canvas.width = trimWidth;
	copy.canvas.height = trimHeight;
	copy.putImageData(trimmed, 0, 0);

	// Return trimmed canvas
	return copy.canvas;
}

/**
 * Formats filters values to state.
 * State is used for showing filter values
 *
 * @param filters
 * @param state
 */
export const formatFiltersToState = ({
	filters,
	state,
  authorList,
}: {
	filters: Filters;
	state: MapFiltersState;
  authorList: AuthorsResData[];
}): MapFiltersState => {
	if (falsy(filters)) {
		return state;
	}

  if (filters?.author?.selected) {
    state.authorSelected = filters?.author?.selected;
    state.authorSelectedLabel = getSelectedAuthorLabel(filters?.author?.selected, authorList);
  }

	state.dataTypeSelected = filters?.dataType?.selected;
	state.dataTypeList = filters?.dataType?.available ?? [];

	state.variablesSelected = filters?.variable?.selected;
	state.variables = filters?.variable?.available ?? [];
	state.variablesIsDisabled = !state.variables?.length;

	state.basePeriodSelected = filters?.period?.selected;
	state.basePeriodList = filters?.period?.available ?? [];

  state.gwlSelected = filters?.gwl?.selected;
  state.gwlList = filters?.gwl?.available ?? [];

	state.scenarioSelected = filters?.scenario?.selected;
	state.scenarioList = filters?.scenario?.available ?? [];

	state.periodIsDisabled = !state.basePeriodList?.length;

	state.seasonSelected = filters?.season?.selected;
	state.seasonList = filters?.season?.available ?? [];
	state.seasonIsDisabled = !state.seasonList?.length;

  state.gridSpacingSelected = filters?.gridSpacing?.selected;
  state.gridSpacingList = filters?.gridSpacing?.available ?? [];

	return state;
};

/**
 * Gets first feature from Layer by pixel
 * and if it doesn't find feature it will move pixel coords and try again.
 *
 * @param pixel
 * @param layer
 * @param movePixelBy
 */
export const getFirstFeatureByPixel = async ({
	pixel,
	layer,
	movePixelBy = [],
}: {
	pixel: number[];
	layer: VectorLayer<VectorSource>;
	movePixelBy?: number[];
}): Promise<Feature> => {
	const squareFeatList = await layer.getFeatures(pixel);
	const [firstSquare] = squareFeatList;

	if (firstSquare || !movePixelBy?.length) {
		return firstSquare as Feature;
	}

	const n = movePixelBy.shift();
	const p = pixel.map((x) => x + n);

	return getFirstFeatureByPixel({
		pixel: p,
		layer,
		movePixelBy,
	});
};

/**
 * Formats CSV name to more user friendly name.
 * (eg.)
 * name => result
 * "mod_tas_vre_1951-2100_na_rcp45_na_g_ens_bra.csv" => "Srednja dnevna temperatura (tas) - za period 1951-2100 i za scenario RCP45"
 * "obs_tas_pro_1961-1990_1950-2020_na_na_g_eobs_bra.csv" => "Srednja dnevna temperatura (tas) - odstupanje za period 1950-2020 u odnosu na 1961-1990"
 *
 * @param name
 */
export const formatCSVName = (name: string): string => {
	if (!name) {
		return '';
	}

  //TODO: OLD CODE STUFF SHOULD BE REFACTORED
	// eslint-disable-next-line prefer-const, @typescript-eslint/no-unused-vars
	let [data, variable, viz, basePeriod, changePeriod, scenario, , season, model, shortName] =
		name.split('.').shift().split('_') || [];

	variable = t(`filters.variable.${variable}Title`);


	return `${variable} - ${viz}${scenario}`;
};

/**
 * Gets name form feature for right local (cir, lat or eng).
 *
 * @param feat
 */
export const getFeatureName = (feat: Feature): string => {
	const name = feat?.get('name');
	return name?.[(i18n.global as any).locale.value] || '';
};

/**
 * Returns inches pre unit on map.
 * Used for calculating how much map scaled.
 *
 * @param unit
 */
export function inchesPreUnit(unit: Units): number {
	return (METERS_PER_UNIT as Record<Units, number>)[unit] * INCHES_PER_METRE;
}

/**
 * Returns map ratio to real size.
 *
 * @param map
 * @param toRound
 */
export function mapRatioScale({ map, toRound = true }: { map: Map; toRound?: boolean }): number {
	const resolution = map.getView().getResolution();
	const unit = map.getView().getProjection().getUnits();

	let scale = resolution * inchesPreUnit(unit) * DOTS_PER_INCH;
	scale = toRound ? Math.round(scale) : scale;

	return scale;
}

export function getColorForValue(value: number, colorRange: Array<ColorRange>): string | null {
  const processedRanges = colorRange
    // .filter(range => range.usage)
    .map(range => ({
      min: range.min,
      max: range.max,
      color: range.color,
      isFirstRange: range.min === null,
      isLastRange: range.max === null
    }))
    .sort((a, b) => {
      if (a.isFirstRange) return -1;
      if (b.isFirstRange) return 1;
      if (a.isLastRange) return 1;
      if (b.isLastRange) return -1;
      return (a.min || 0) - (b.min || 0);
    });

  for (const range of processedRanges) {
    let matches = false;

    if (range.isFirstRange) {
      matches = value <= range.max;
    } else if (range.isLastRange) {
      matches = value > range.min;
    } else {
      matches = value > range.min && value <= range.max;
    }

    if (matches) {
      return range.color;
    }
  }

  return null;
}

/**
 * Generates color grid from value grid and color range
 *
 * @param valueGrid - Object where key is index and value is the data value
 * @param colorRange - Array of color range objects with min, max, color, and usage properties
 * @param chunkSize - Number of entries to process in each chunk (default: 10000)
 * @returns Array of color strings for each grid position
 */
export function generateColorGrid(
	valueGrid: Record<number, number>,
	colorRange: Array<ColorRange>,
	chunkSize = 10000
):string[] {
	if (!valueGrid || Object.keys(valueGrid).length === 0) {
		return;
	}

	const keys = Object.keys(valueGrid);
	const maxIndex = Number(keys[keys.length - 1]);


	const colorGrid = new Array(maxIndex + 1).fill(getColorForValue(0, colorRange));

	const entries = Object.entries(valueGrid);
	const totalEntries = entries.length;

	for (let startIndex = 0; startIndex < totalEntries; startIndex += chunkSize) {
		const endIndex = Math.min(startIndex + chunkSize, totalEntries);

		for (let i = startIndex; i < endIndex; i++) {
			const [indexStr, value] = entries[i];
			const gridIndex = Number(indexStr);

      const color = getColorForValue(value, colorRange);
      if (color) {
        colorGrid[gridIndex] = color;
      }
		}
	}

	return colorGrid;
}

export function makeColorLighter(color?: string, shades = 3): string {
  if (!color) {
    return color;
  }
  // Handle format: '254,235,162'
  if (color?.includes(',') && !color.includes('rgb')) {
    const [r, g, b] = color.split(',').map(x => parseFloat(x.trim()));
    if (!isNaN(r) && !isNaN(g) && !isNaN(b)) {
      // Blend with white to make lighter (more natural)
      const blendFactor = Math.min(0.8, shades * 0.1); // Max 80% white blend
      const lighterR = Math.round(r + (255 - r) * blendFactor);
      const lighterG = Math.round(g + (255 - g) * blendFactor);
      const lighterB = Math.round(b + (255 - b) * blendFactor);
      return `rgba(${lighterR},${lighterG},${lighterB})`;
    }
  }

  // Handle rgba format
  const rgba = color.match(/rgba?\(([^)]+)\)/);
  if (rgba) {
    const [r, g, b, a] = rgba[1].split(',').map(x => parseFloat(x.trim()));
    const blendFactor = Math.min(0.8, shades * 0.1);
    const lighterR = Math.round(r + (255 - r) * blendFactor);
    const lighterG = Math.round(g + (255 - g) * blendFactor);
    const lighterB = Math.round(b + (255 - b) * blendFactor);
    return `rgba(${lighterR}, ${lighterG}, ${lighterB}, ${a || 1})`;
  }

  return color;
}
